import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Modal from "../../../components/Modal/Modal"
import {
  getMosaListWithUsers,
  addUserToMosa,
  removeUserFromMosa,
  Mosa,
} from "../service/mosa-api"
import { getAllSectorAssociationRepresentatives } from "../service/api"
import { User } from "../types"

export const ManageSAR: React.FC = () => {
  const { t } = useTranslation()
  const [mosaList, setMosaList] = useState<Mosa[]>([])
  const [sarUsers, setSarUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedMosa, setSelectedMosa] = useState<Mosa | null>(null)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

  const loadData = async () => {
    try {
      setLoading(true)
      const [mosasResponse, usersResponse] = await Promise.all([
        getMosaListWithUsers({}),
        getAllSectorAssociationRepresentatives({}),
      ])
      setMosaList(mosasResponse.data || [])
      setSarUsers(usersResponse.data || [])
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleManageUsers = (mosa: Mosa) => {
    setSelectedMosa(mosa)
    const assignedUserIds = mosa.users?.map((u) => u.id) || []
    setSelectedUserIds(assignedUserIds)
    setShowModal(true)
  }

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSave = async () => {
    if (!selectedMosa) return

    try {
      const currentUserIds = selectedMosa.users?.map((u) => u.id) || []
      const usersToAdd = selectedUserIds.filter(
        (id) => !currentUserIds.includes(id)
      )
      const usersToRemove = currentUserIds.filter(
        (id) => !selectedUserIds.includes(id)
      )
      for (const userId of usersToAdd) {
        await addUserToMosa({
          mosaId: selectedMosa.id,
          userId: parseInt(userId),
        })
      }

      for (const userId of usersToRemove) {
        await removeUserFromMosa({
          mosaId: selectedMosa.id,
          userId: parseInt(userId),
        })
      }

      setShowModal(false)
      await loadData()
    } catch (error: any) {
      console.error("Error updating users:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Error updating users"
      alert(errorMessage)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="manage-sar">
      <table className="table table-light table-bordered">
        <thead>
          <tr>
            <th>{t("Sector Association")}</th>
            <th>{t("Users")}</th>
            <th>{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {mosaList.map((mosa) => (
            <tr key={mosa.id}>
              <td>{mosa.name}</td>
              <td>
                {mosa.users && mosa.users.length > 0 ? (
                  <ul className="mb-0">
                    {mosa.users.map((user) => (
                      <li key={user.id}>
                        {user.name} ({user.email})
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted">{t("No users assigned")}</span>
                )}
              </td>
              <td>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: "#74BE0F", color: "white" }}
                  onClick={() => handleManageUsers(mosa)}
                >
                  {t("Attach/Unattach User(s)")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && selectedMosa && (
        <Modal isOpen={showModal} isOpenHandler={setShowModal} wrapperClass="srs-modal-lg">
          <div className="p-5">
            <h2>{t("Manage Users for")} {selectedMosa.name}</h2>

            <div className="mt-4">
              <p>{t("Select users to attach to this sector association")}:</p>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {sarUsers.map((user) => (
                  <div key={user.id} className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`user-${user.id}`}
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => toggleUser(user.id)}
                    />
                    <label className="form-check-label" htmlFor={`user-${user.id}`}>
                      {user.name} ({user.email})
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                className="btn btn-secondary mr-2"
                onClick={() => setShowModal(false)}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn"
                style={{ backgroundColor: "#74BE0F", color: "white" }}
                onClick={handleSave}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
